import type { PostMultiplacerDrofusInterfaceOccurrenceByIfcGuidApiResponse } from "../../openapi";
type Occurrence = NonNullable<PostMultiplacerDrofusInterfaceOccurrenceByIfcGuidApiResponse>;

export interface MatchContext {
  projectNo: number;
  itemId: string | null;
  ready: boolean;
  ifcGuid: string | null;
  roomFunctionNo: string | null;
  articleCode: string | null;
}
export interface MatchQuery<T> {
  data?: T;
  isLoading?: boolean;
  isUninitialized?: boolean;
  isError?: boolean;
  error?: unknown;
}
export function normalizeArticleCode(value: unknown): string | null {
  if (value && typeof value === "object" && "value" in value) return normalizeArticleCode(value.value);
  return typeof value === "string" && value.trim() ? value.trim() : null;
}
export function errorStatus(query?: MatchQuery<unknown>): unknown {
  const error = query?.error;
  return error && typeof error === "object" && "status" in error ? error.status : null;
}
export function needsRoomFallback(context: MatchContext, guid?: MatchQuery<Occurrence | null>): boolean {
  return context.ready && !!context.projectNo && !!context.itemId && (!context.ifcGuid
    || (guid?.data === null && !guid.isLoading && !guid.isUninitialized && !guid.isError));
}
export interface MatchRow { occurrenceId: number; runId: string; quantity: number | null; }
export interface MatchView {
  heading: string | null;
  message: string | null;
  retry: "guid" | "room" | null;
  rows: MatchRow[];
}
const pending = (query?: MatchQuery<unknown>) => !query || query.isUninitialized || query.isLoading;
const row = (item: Occurrence): MatchRow => ({ occurrenceId: item.occurrenceId, runId: item.runId, quantity: item.normalized.occurrence.quantity ?? null });

export function buildDrofusMatchView(context: MatchContext, guid?: MatchQuery<Occurrence | null>, room?: MatchQuery<Occurrence[]>): MatchView {
  const view: MatchView = { heading: null, message: null, retry: null, rows: [] };
  if (!context.projectNo) return { ...view, message: "Velg et prosjekt." };
  if (!context.itemId) return { ...view, message: "Velg et objekt." };
  if (!context.ready) return { ...view, message: "Laster objektdata…" };
  if (context.ifcGuid && !needsRoomFallback(context, guid)) {
    if (pending(guid)) return { ...view, message: "Søker etter forekomst via IFC-GUID…" };
    if (guid?.isError) {
      const status = errorStatus(guid);
      return { ...view, retry: status === 400 ? null : "guid", message: status === 409
        ? "Flere forekomster er koblet til samme IFC-GUID. Koblingen er tvetydig."
        : status === 400 ? "Objektet har en ugyldig IFC-GUID."
        : status === 404 ? "Ingen vellykket dRofus-import funnet for prosjektet."
        : "Kunne ikke hente dRofus-match. Prøv igjen." };
    }
    return guid?.data ? { ...view, heading: "Koblet forekomst", rows: [row(guid.data)] } : view;
  }
  if (!context.roomFunctionNo) return { ...view, message: "Objektets rom er ikke identifisert. Kan ikke søke via rom og artikkel." };
  if (!context.articleCode) return { ...view, message: "Objektet mangler artikkelnummer. Kan ikke matche forekomstene i rommet." };
  if (pending(room)) return { ...view, message: "Søker etter forekomster i rommet…" };
  if (room?.isError) return { ...view, retry: "room", message: errorStatus(room) === 404
    ? "Ingen vellykket dRofus-import funnet for prosjektet." : "Kunne ikke hente forekomstene i rommet. Prøv igjen." };
  const candidates = (room?.data ?? []).filter(item => normalizeArticleCode(item.normalized.article.compCode) === context.articleCode);
  return {
    ...view,
    heading: "Mulige treff via rom og artikkel",
    message: candidates.length ? "Dette er mulige treff, ikke en bekreftet kobling til objektet."
      : "Ingen forekomster med samme artikkelnummer funnet i rommet i siste import.",
    rows: candidates.map(row),
  };
}
